import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';

const AI_PATH = process.env.REACT_APP_AI_PATH;
const inr = n => '₹' + Math.round(n || 0).toLocaleString('en-IN');

// Strip shows this many tiles before the "View all" card — matches the
// buyume-web picker's horizontal-strip length.
const STRIP_COUNT = 6;

// Per-product quantity ceiling — not a business rule, just a sanity bound so
// nobody fat-fingers into asking for hundreds of a ₹1 item. The real limit is
// always the MRP cap itself.
const MAX_QTY_PER_PRODUCT = 20;

/**
 * FBL Prime free-gift picker for the sheet-upload flow — CRM twin of
 * buyume-web's PrimeFreePicker.jsx (cart page). Same rules: qualifying order
 * value, MRP cap from the live Subscription doc, only primeFreeEligible
 * products. Reads the SAME backend endpoint the website uses
 * (/web/prime-free-products) so the two clients can't disagree about what a
 * member is owed. The cap is re-enforced here purely for UI responsiveness —
 * the real gate is server-side in sheetUploadOrders._resolvePrimeFreeGifts
 * when the draft is created.
 *
 * `selected` is [{productId, name, mrp, EANCode, qty}] — a buyer can take
 * more than one of the same free product (2026-09-07: was previously a
 * one-each toggle), capped only by how many units fit under the MRP
 * allowance, e.g. 3× a ₹799 item = ₹2,397 of a ₹2,500 cap.
 *
 * `treatAsNewMember` (2026-09-08): set when the agent has checked "sell FBL
 * Prime with this order" for a seller who ISN'T a member yet — lets this
 * same component preview what they'd get, using the same live cap/eligible-
 * list logic a real member sees. Purely a display bypass: it doesn't grant
 * anything, and it's a no-op for an actual existing member. The real
 * decision at order-creation time is server-side
 * (sheetUploadOrders._checkPrimeMembershipPurchaseEligible/
 * _resolvePrimeFreeGifts), independent of this flag.
 *
 * Plans (2026-09-24): `tier` = the plan being bought with this order
 * (silver/gold/diamond) — used only with treatAsNewMember. A real member's
 * own plan is read server-side. Only the member's OWN plan counts: the order
 * must reach that plan's minimum, and the cap is what's left of that plan's
 * month (services/primeTiers.js) — a higher plan never gets a lower plan's level.
 *
 * Renders nothing for non-members (unless treatAsNewMember) or when there's
 * nothing eligible.
 */
export default function PrimeFreeGiftPicker({ sellerId, cartTotal = 0, selected = [], onChange, treatAsNewMember = false, tier = null }) {
  const [data, setData] = useState(null);
  const [warn, setWarn] = useState('');
  const [viewAll, setViewAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    if (!sellerId) return undefined;
    const params = { sellerId, cartTotal };
    if (treatAsNewMember) {
      params.treatAsNewMember = 'true';
      if (tier) params.tier = tier;
    }
    axios.get(`${AI_PATH}/api/ai/web/prime-free-products`, { params })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [sellerId, cartTotal, treatAsNewMember, tier]);

  // Plan or order value changed and the picks no longer fit the new cap —
  // clear them rather than let the server silently drop some.
  useEffect(() => {
    if (!data || !onChange) return;
    const cap = data.isMember && (data.products || []).length ? Number(data.freeCap || 0) : 0;
    const picked = (selected || []).reduce((s, p) => s + Number(p.mrp || 0) * Number(p.qty || 1), 0);
    if (picked > cap) onChange([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!data?.isMember || !(data.products || []).length) return null;

  const CAP = data.freeCap || 0;
  const qualified = !!data.qualified;
  const usedMrp = (selected || []).reduce((s, p) => s + Number(p.mrp || 0) * Number(p.qty || 1), 0);
  const freeLeft = Math.max(0, CAP - usedMrp);
  const qtyOf = id => {
    const item = (selected || []).find(p => p.productId === id);
    return item ? Number(item.qty || 1) : 0;
  };

  const changeQty = (p, delta) => {
    if (!qualified || !onChange || p.outOfStock) return;
    setWarn('');
    const current = qtyOf(p.productId);
    const nextQty = current + delta;

    if (nextQty <= 0) {
      onChange((selected || []).filter(x => x.productId !== p.productId));
      return;
    }
    if (nextQty > MAX_QTY_PER_PRODUCT) return;

    const mrp = Number(p.mrp || 0);
    const wouldBe = usedMrp - mrp * current + mrp * nextQty;
    if (delta > 0 && wouldBe > CAP) {
      setWarn(`That would total ${inr(wouldBe)} — over the ${inr(CAP)} free limit.`);
      return;
    }

    const exists = current > 0;
    const updated = exists
      ? (selected || []).map(x => x.productId === p.productId ? { ...x, qty: nextQty } : x)
      : [...(selected || []), { productId: p.productId, name: p.name, mrp: p.mrp, EANCode: p.EANCode, qty: nextQty }];
    onChange(updated);
  };

  const Tile = ({ p }) => {
    const qty = qtyOf(p.productId);
    const sel = qty > 0;
    const wouldExceed = usedMrp + Number(p.mrp || 0) > CAP;
    return (
      <Box style={{
        width: 150, flex: '0 0 auto', borderRadius: 8, background: '#fff', padding: 8,
        border: sel ? '1.5px solid #1b6e2e' : '1px solid #ddd',
        opacity: p.outOfStock ? 0.55 : 1,
      }}>
        <Box style={{
          height: 64, marginBottom: 6, display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: '#fafafa', borderRadius: 6,
        }}>
          {p.image
            ? <img src={p.image} alt={p.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                   onError={e => { e.currentTarget.style.display = 'none'; }} />
            : <span style={{ fontSize: 10, color: '#999', textAlign: 'center', padding: '0 4px' }}>
                {(p.name || '').split(' ').slice(0, 3).join(' ')}
              </span>}
        </Box>
        <Typography style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.25, height: 28, overflow: 'hidden' }}>
          {p.name}
        </Typography>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
          <Box>
            <Typography style={{ fontSize: 12, fontWeight: 700, color: p.outOfStock ? '#c62828' : '#1b6e2e' }}>
              {p.outOfStock ? 'OUT OF STOCK' : 'FREE'}
            </Typography>
            <Typography style={{ fontSize: 10, color: '#999', textDecoration: 'line-through' }}>{inr(p.mrp)}</Typography>
          </Box>
          {p.outOfStock ? null : sel ? (
            <Box style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton size="small" disabled={!qualified} onClick={() => changeQty(p, -1)} style={{ padding: 3 }}>
                <RemoveIcon style={{ fontSize: 14 }} />
              </IconButton>
              <Typography style={{ fontSize: 12, fontWeight: 700, minWidth: 14, textAlign: 'center' }}>{qty}</Typography>
              <IconButton size="small" disabled={!qualified || wouldExceed || qty >= MAX_QTY_PER_PRODUCT} onClick={() => changeQty(p, 1)} style={{ padding: 3 }}>
                <AddIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ) : (
            <Button
              size="small"
              variant="outlined"
              disabled={!qualified || wouldExceed}
              onClick={() => changeQty(p, 1)}
              style={{ minWidth: 0, fontSize: 10, padding: '2px 8px' }}
            >
              Add
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const strip = data.products.slice(0, STRIP_COUNT);
  const hasMore = data.products.length > STRIP_COUNT;

  return (
    <Box style={{
      margin: '0 0 16px', borderRadius: 10, overflow: 'hidden',
      background: 'linear-gradient(to bottom, #fff8e1, #fff3cd)',
      border: '1px solid #ffe69c', padding: '12px 14px',
    }}>
      <Typography style={{ fontWeight: 700, fontSize: 14 }}>
        🎁 FBL Prime{data.tierLabel ? ` ${data.tierLabel}` : ''} free products
      </Typography>
      {qualified && data.monthlyCap > 0 && (
        <Typography style={{ fontSize: 11, color: '#7a5a00' }}>
          {inr(data.remainingThisMonth)} left this month of {inr(data.monthlyCap)}.
        </Typography>
      )}
      <Typography style={{
        fontSize: 12, marginTop: 2, marginBottom: 8,
        fontWeight: qualified ? 700 : 400, color: qualified ? '#1b6e2e' : '#7a5a00',
      }}>
        {qualified
          ? `Pick any products up to ${inr(CAP)} total — free!`
          : `Order needs to reach ${inr(data.qualifyingThreshold)} to unlock free products (${inr(data.remaining)} more)`}
      </Typography>

      {qualified && (
        <Box style={{ marginBottom: 8 }}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700 }}>
            <span>{inr(usedMrp)} used of {inr(CAP)}</span>
            <span style={{ color: '#666', fontWeight: 600 }}>{inr(freeLeft)} left</span>
          </Box>
          <Box style={{ height: 6, background: 'rgba(255,255,255,0.7)', borderRadius: 4, overflow: 'hidden', marginTop: 3 }}>
            <Box style={{ height: '100%', width: `${Math.min(100, Math.round((usedMrp / CAP) * 100))}%`, background: '#1b6e2e' }} />
          </Box>
        </Box>
      )}

      {warn && (
        <Typography style={{
          fontSize: 11, color: '#7a5a00', background: '#fff3cd', border: '1px solid #ffe69c',
          borderRadius: 4, padding: '4px 8px', marginBottom: 8,
        }}>
          {warn}
        </Typography>
      )}

      {/* Single-row horizontal strip — scrolls left/right instead of wrapping. */}
      <Box style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
        {strip.map(p => <Tile key={p.productId} p={p} />)}

        {hasMore && (
          <Box
            onClick={() => setViewAll(true)}
            style={{
              width: 110, flex: '0 0 auto', borderRadius: 8, border: '1px dashed #ccc',
              background: '#fff', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: 22 }}>🎁</span>
            <Typography style={{ fontSize: 12, fontWeight: 700, color: '#1b6e2e', marginTop: 4 }}>
              View all ({data.products.length}) ›
            </Typography>
          </Box>
        )}
      </Box>

      {/* View-all dialog — full grid of every eligible product. */}
      <Dialog open={viewAll} onClose={() => setViewAll(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          🎁 Choose your free products
          <IconButton size="small" onClick={() => setViewAll(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 12, marginBottom: 10, fontWeight: 700, color: qualified ? '#1b6e2e' : '#7a5a00' }}>
            {qualified ? `${inr(usedMrp)} used of ${inr(CAP)}` : `Shop for ${inr(data.remaining)} more to claim`}
          </Typography>
          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 10, paddingBottom: 8 }}>
            {data.products.map(p => <Tile key={p.productId} p={p} />)}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
